import type { CarType } from "../types/CarType";
import type { ItemsType } from "../../../common-deprecated/redux/typeHelpers";
import type { BasketType, MinimizedBasketType } from "../types/BasketType";
import type { QueryType } from "../../../common-deprecated/server/types";
import { find } from "../../../common-deprecated/utils";
import type {
    AccentColourType,
    ColourType,
    ExtrasCategoryToFilterType,
    ExtraType,
    PackType,
    PromotionType,
    ServiceProductType,
    UpholsteryType,
    WheelType,
    WinterWheelType,
} from "../../../common-deprecated/types/CommonTypes";
import CarDataConsolidator from "./classes/CarDataConsolidator";

/**
 * MinimizeBasket but inverse.
 * This is a base for the baskets without the Kanzen and other build specific things
 * TODO A fun test might be testing these functions against eachother.
 */
export const maximizeBasket = (
    initCar: CarType,
    items: ItemsType,
    basket: BasketType,
    query: QueryType,
): BasketType => {
    // If the car has been loaded by a tyCode loadedCar should be set.
    const car = initCar.tyCodeCar || initCar;

    // Resolve selected packs first because all the consecutive elements (extras, colours, wheels) can be part of the pack
    const packs =
        // Check the querystring for packs; else rely on the packs on the car object
        (
            Array.isArray(query.p)
                ? // Check the querystring first
                  query.p.map((id) => find(parseInt(id), items.packs))
                : // If no querystring is supplied, check the packs defined on the car
                  car.packs.map((p) => find(p.id, items.packs))
        )
            // Filter out the non existend packs
            .filter(Boolean) as PackType[];

    const serviceProducts = (
        Array.isArray(query.sp)
            ? query.sp.map((id) => find(parseInt(id), items.serviceProducts))
            : car.serviceProducts.map((serviceProduct) => find(serviceProduct.id, items.serviceProducts))
    ).filter(Boolean) as ServiceProductType[];

    // Get the colour from the available colours
    const availableColours = CarDataConsolidator.getColours(items.colours, { packs } as BasketType);
    const colour: ColourType = (query.c && find(query.c, availableColours)) || car.colour;
    // Find the current upholstery in the packs if it is not found in the accessories!
    const availableUpholsteries = CarDataConsolidator.getUpholsteries(items.upholsteries, items.equipmentUpholsteries, {
        packs,
        colour,
    } as BasketType);

    // Also try to find the default upholstery in the availableUpholsteries in case the default upholstery is part of a pack.
    const upholstery: UpholsteryType =
        // Check querystring first, fallback to the car.upholstery
        find(query.u || car.upholstery.id, availableUpholsteries) ||
        // If it's not available, check if there is a colour present and take it's first upholstery
        find(colour.upholsteries[0], availableUpholsteries) ||
        // Finally default to the car.upholstery
        (car.upholstery as any);

    const availableWheels = CarDataConsolidator.getWheels(
        items.wheels,
        { packs, colour, upholstery } as BasketType,
        items.packs,
    );
    const standardPackWheel = items.packs.find(
        (pack) => pack.standard && pack.equipment.find((equipment) => equipment.isWheel && equipment.standard),
    );

    const wheel: WheelType =
        // Check the querystring
        (find(
            typeof query.w === "object" ? parseInt(query.w.i) : parseInt(query.w || ""),
            availableWheels,
        ) as WheelType) ||
        (standardPackWheel && availableWheels.find((availableWheel) => availableWheel.standard)) ||
        // Default to the wheel defined on the car
        car.wheel;

    const installationOfTheWinterWheelsFromLoadedCar = car.winterWheel ? car.winterWheel.installTheWheels : true;
    let winterWheel: WinterWheelType | null = query.ww ? find(query.ww, items.winterWheels)! : car.winterWheel;

    // Check installation of winterwheel
    if (winterWheel) {
        winterWheel = {
            ...winterWheel,
            installTheWheels: car && car.winterWheel ? installationOfTheWinterWheelsFromLoadedCar : !!query.iw,
        };
    }

    // Check the extras, if it's an equipment upholstery, which is returned by the CarDataConsolidator.getUpholsteries method
    const combinedExtras: (ExtraType | UpholsteryType)[] = items.extras.concat(availableUpholsteries as any);
    // Don't execute this logic when there is already an explicit upholstery selected in the URL.
    const standardEquipmentUpholstery: any =
        !query.u && items.equipmentUpholsteries.length
            ? items.equipmentUpholsteries.find((equipmentUpholstery) => equipmentUpholstery.standard)
            : undefined;

    // List all available pack equipment of the selected packs
    // This is manually casted to ExtraType because even though packExtra's can be wheels, it's never the case in basket because those extras would be part of the wheel object.
    const allPackEquipment: ExtraType[] = packs.reduce(
        (packEquipment: ExtraType[], pack) => packEquipment.concat(pack ? (pack.equipment as ExtraType[]) : []),
        [],
    );
    // find selected pack extras
    const packExtras =
        // Check the querystring first
        (
            Array.isArray(query.pe)
                ? query.pe.map((id) => find(parseInt(id), allPackEquipment))
                : // If no querystring is supplied, check the extras defined on the car
                  car.extras.map((e) => find(e.id, allPackEquipment))
        )
            // Filter out the non existend extras
            .filter(Boolean) as ExtraType[];

    const extras: ExtraType[] =
        // Check the querystring for equipment; else rely on the extras on the car object
        (
            Array.isArray(query.e)
                ? // Check the querystring first
                  query.e.map((shortId) => {
                      if (standardEquipmentUpholstery && standardEquipmentUpholstery.shortId === shortId) return null;
                      const extra: ExtraType = find(parseInt(shortId), combinedExtras) as any;
                      // If it's an accessory upholstery make sure to check for availability
                      // configuration can change and availability as well this prevents loading an old url to resolve into an out of date configuration
                      if (
                          !extra ||
                          (extra.isUpholstery && !CarDataConsolidator.isAvailable(extra, colour, upholstery).available)
                      )
                          return null;
                      // TODO selectedOptionId seems to be a "magic" property, only appended in this conditional
                      let selectedOptionId = "";
                      if (extra && Array.isArray(query.ec) && extra.options) {
                          selectedOptionId = query.ec.find((childOptionId) => find(childOptionId, extra.options))!;
                      }
                      return { ...extra, selectedOptionId };
                  })
                : // If no querystring is supplied, check the extras defined on the car
                  car.extras.map((e) => find(e.id, combinedExtras)! as ExtraType)
        )
            .concat(
                standardEquipmentUpholstery &&
                    CarDataConsolidator.isAvailable(standardEquipmentUpholstery, colour, upholstery).available
                    ? [standardEquipmentUpholstery]
                    : [],
            )
            // Check the proace selected equipment
            .concat(
                query.selectedEquipment
                    ? query.selectedEquipment
                          .split(",")
                          .map((se) => find(parseInt(se), combinedExtras) as any)
                          .filter(Boolean)
                    : [],
            )
            // Filter out the non existend extras
            .filter(Boolean)
            // filter out selected pack extras
            .filter((extra) => !packExtras.find((pe) => pe.id === extra!.id)) as ExtraType[];

    // Find primary accent colour
    // Create a list of all available primary accent colours throughout the selected packs
    const primaryAccentColours = packs.reduce(
        (accentColours: AccentColourType[], pack) =>
            accentColours.concat(
                pack && colour && pack.extended && pack.extended.accentColours[colour.id]
                    ? pack.extended.accentColours[colour.id]
                    : [],
            ),
        [],
    );

    // Get the selected primary accent colour
    const primaryAccentColour = query.pac
        ? // Check the querystring
          find(query.pac, primaryAccentColours)!
        : // Check inside the extras configured on the car object
          CarDataConsolidator.getAccentColourFromExtras(car.extras, packs);

    // Find secondary accent colour
    const secondaryAccentColour: AccentColourType | null = query.sac
        ? // Check the querystring
          find(query.sac, primaryAccentColour ? primaryAccentColour.accentColours : [])!
        : // Check inside the extras configured on the car object
          primaryAccentColour
          ? CarDataConsolidator.getAccentColourFromExtras(car.extras, packs, primaryAccentColour)
          : null;
    const userSelectedPromotions = Array.isArray(query.prom)
        ? query.prom.reduce((allPromotions: PromotionType[], promoId) => {
              const correctPromotion = car.manualPromotions.find((promo) => promo.id === promoId);
              if (correctPromotion) {
                  allPromotions.push(correctPromotion);
              }
              return allPromotions;
          }, [])
        : [];

    return {
        ...basket,
        colour,
        primaryAccentColour,
        secondaryAccentColour,
        upholstery,
        wheel,
        winterWheel,
        extras,
        packs,
        serviceProducts,
        packExtras,
        userSelectedPromotions,
    };
};

export const applyFilterToExtras = (
    extras: ExtraType[],
    searchString: string,
    filterExtrasCategories: ExtrasCategoryToFilterType[],
    filterRootCategory: ExtrasCategoryToFilterType | null,
): ExtraType[] => {
    // Filter for search value when a search value is entered.
    if (searchString) {
        extras = extras.filter((extra) => extra.name.toLowerCase().includes(searchString.toLowerCase()));
    }
    // Filter the selected extras categories
    if (filterExtrasCategories && filterExtrasCategories.length) {
        extras = extras.filter((extra) => {
            return filterExtrasCategories
                .map((filterExtrasCategory) => filterExtrasCategory.name)
                .includes(extra.category.name);
        });
    }
    // Filter the selected root category
    if (filterRootCategory) {
        extras = extras.filter((extra) => {
            return extra.category.root && filterRootCategory && filterRootCategory.id === extra.category.root.id;
        });
    }
    return extras;
};

export const minimizeBasket = (basket: BasketType, car: CarType): MinimizedBasketType => {
    const value: MinimizedBasketType = {};

    if (!basket.colour.id || !car.id) return value;

    if (basket.colour.id !== car.colour.id) value.c = basket.colour.id;
    else value.c = null; // Explicitly define this as null so it will get removed from the query params

    // To support selection between upholsteries and standard equipment upholsteries, we'll need to keep track as soon as an upholstery is selected
    value.u = basket.upholstery.id;
    if (basket.wheel.id !== car.wheel.id) {
        value.w = basket.wheel.shortId;
    }
    if (basket.winterWheel) {
        value.ww = basket.winterWheel.id;
        value.iw = basket.winterWheel.installTheWheels;
    }
    value.p = basket.packs.map((pack) => pack.shortId);
    value.pe = basket.packExtras.map((packExtra) => packExtra.shortId);
    // Service plans
    value.sp = basket.serviceProducts.map((serviceProduct) => serviceProduct.shortId);

    value.e = basket.extras.map((extra) => extra.shortId);

    value.ec = basket.extras.reduce((ec: string[], extra) => {
        const extraChildOption = CarDataConsolidator.getSelectedExtraOption(extra);
        if (extraChildOption) ec.push(extraChildOption.shortId);
        return ec;
    }, []);

    value.prom = basket.userSelectedPromotions.map((promo) => promo.id);

    return value;
};

export const isOnlinePurchaseAvailable = (basket: BasketType): boolean => {
    // If there is currently a colour or an upholstery selected which is only available through a selected pack, always return true.
    // Further analysis/implementation for Visibility flag propagation on colours/upholsteries in packs is logged in OR-4134
    const isPackColour = CarDataConsolidator.getBasketPackColours(basket.packs).find(
        (packColour) => packColour.id === basket.colour.id,
    );
    if (isPackColour) return true;
    const isPackUpholstery = CarDataConsolidator.getBasketPackUpholsteries(basket.packs).find(
        (packUpholstery) => packUpholstery.id === basket.upholstery.id,
    );
    if (isPackUpholstery) return true;

    return !!basket.colour.upholsteryOnlineAvailability.find((upholstery) => upholstery === basket.upholstery.id);
};
